'use client';

import { Box, HStack, css } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { MetaDefault, MonoSmall } from '../../../components/typography';
import type { CopilotMode } from '@terra/shared-dto-chat-types';

const statusLineContainerStyles = css({
  width: '100%',
  minWidth: 0,
  paddingInline: 2,
  paddingTop: 1,
  paddingBottom: 0.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  flexWrap: 'wrap',
  userSelect: 'none',
});

const separatorStyles = css({
  color: 'fg.subtle',
});

export type ChatComposerStatusLineData = {
  mode: CopilotMode;
  modeLabel?: string;
  contextPercent?: number | null;
  contextUsedTokensFormatted?: string | null;
  contextWindowTokensFormatted?: string | null;
  isOverCompactionTrigger?: boolean;
  cacheHitRatioPercent?: number | null;
  backgroundTasksCount?: number;
  modelName?: string | null;
};

export type StatusLineFormatLabels = {
  formatContext?: (args: {
    percent: number;
    used?: string | null;
    window?: string | null;
  }) => string;
  formatCache?: (args: { percent: number }) => string;
  formatTasks?: (args: { count: number }) => string;
};

export function formatStatusLineSegments(
  data: ChatComposerStatusLineData,
  labels?: StatusLineFormatLabels,
): Array<{ id: string; text: string; isAlert?: boolean; isMono?: boolean }> {
  const segments: Array<{
    id: string;
    text: string;
    isAlert?: boolean;
    isMono?: boolean;
  }> = [];

  const modeDisplay =
    data.modeLabel ??
    `${data.mode.charAt(0).toUpperCase()}${data.mode.slice(1)}`;

  segments.push({
    id: 'mode',
    text: modeDisplay,
  });

  if (
    typeof data.contextPercent === 'number' &&
    Number.isFinite(data.contextPercent)
  ) {
    const clampedPercent = Math.min(
      Math.max(Math.round(data.contextPercent), 0),
      100,
    );
    const hasTokens = Boolean(
      data.contextUsedTokensFormatted && data.contextWindowTokensFormatted,
    );
    const defaultText = hasTokens
      ? `Context ${clampedPercent}% (${data.contextUsedTokensFormatted}/${data.contextWindowTokensFormatted})`
      : `Context ${clampedPercent}%`;

    segments.push({
      id: 'context',
      text: labels?.formatContext
        ? labels.formatContext({
            percent: clampedPercent,
            used: data.contextUsedTokensFormatted,
            window: data.contextWindowTokensFormatted,
          })
        : defaultText,
      isAlert: Boolean(data.isOverCompactionTrigger),
      isMono: true,
    });
  }

  if (
    typeof data.cacheHitRatioPercent === 'number' &&
    Number.isFinite(data.cacheHitRatioPercent)
  ) {
    const clampedCache = Math.min(
      Math.max(Math.round(data.cacheHitRatioPercent), 0),
      100,
    );

    segments.push({
      id: 'cache',
      text: labels?.formatCache
        ? labels.formatCache({ percent: clampedCache })
        : `Cache ${clampedCache}%`,
      isMono: true,
    });
  }

  if (
    typeof data.backgroundTasksCount === 'number' &&
    data.backgroundTasksCount > 0
  ) {
    const count = data.backgroundTasksCount;

    segments.push({
      id: 'tasks',
      text: labels?.formatTasks
        ? labels.formatTasks({ count })
        : count === 1
          ? '1 task running'
          : `${count} tasks running`,
    });
  }

  if (data.modelName && data.modelName.trim().length > 0) {
    segments.push({
      id: 'model',
      text: data.modelName.trim(),
      isMono: true,
    });
  }

  return segments;
}

export function ChatComposerStatusLine({
  data,
  onClickContext,
  onClickTasks,
}: {
  data: ChatComposerStatusLineData;
  onClickContext?: () => void;
  onClickTasks?: () => void;
}) {
  const t = useTranslations('designSystem.chat.statusLine');
  const segments = formatStatusLineSegments(data, {
    formatContext: ({ percent, used, window: windowTokens }) =>
      used && windowTokens
        ? t('contextWithTokens', {
            percent,
            used,
            window: windowTokens,
          })
        : t('contextPercent', { percent }),
    formatCache: ({ percent }) => t('cachePercent', { percent }),
    formatTasks: ({ count }) => t('tasksRunning', { count }),
  });

  if (segments.length === 0) {
    return null;
  }

  return (
    <Box
      data-testid="chat-composer-status-line"
      className={statusLineContainerStyles}
    >
      <HStack gap={1.5} alignItems="center" flexWrap="wrap" minW={0}>
        {segments.map((segment, index) => {
          const isInteractive =
            (segment.id === 'context' && Boolean(onClickContext)) ||
            (segment.id === 'tasks' && Boolean(onClickTasks));
          const handleClick =
            segment.id === 'context'
              ? onClickContext
              : segment.id === 'tasks'
                ? onClickTasks
                : undefined;

          return (
            <HStack key={segment.id} gap={1.5} alignItems="center">
              {index > 0 ? (
                <MetaDefault className={separatorStyles} aria-hidden="true">
                  ·
                </MetaDefault>
              ) : null}
              <Box
                data-testid={`status-line-${segment.id}`}
                cursor={isInteractive ? 'pointer' : 'default'}
                onClick={handleClick}
              >
                {segment.isMono ? (
                  <MonoSmall color={segment.isAlert ? 'fg.error' : 'fg.subtle'}>
                    {segment.text}
                  </MonoSmall>
                ) : (
                  <MetaDefault
                    color={segment.isAlert ? 'fg.error' : 'fg.subtle'}
                  >
                    {segment.text}
                  </MetaDefault>
                )}
              </Box>
            </HStack>
          );
        })}
      </HStack>
    </Box>
  );
}
